import { Boundaries } from "@planzone/core";
import { events } from "@planzone/events";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRequest(
  state: State,
  project: Boundaries["retrieveProjectExternalLinks"]["request"],
): State {
  const index = state.findIndex((res) => res.project.uuid === project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          project,
          links: [],
          request: setRequestStateToPending(),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          links: [],
          request: setRequestStateToPending(),
        },
      ];
}

function handleSuccess(
  state: State,
  { project, links }: Boundaries["retrieveProjectExternalLinks"]["success"],
): State {
  const index = state.findIndex((res) => res.project.uuid === project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          project,
          links,
          request: setRequestStateToSuccess(),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          links,
          request: setRequestStateToSuccess(),
        },
      ];
}

function handleFailure(
  state: State,
  project: Boundaries["retrieveProjectExternalLinks"]["failure"],
): State {
  const index = state.findIndex((res) => res.project.uuid === project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          request: setRequestStateToFailure(),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          links: [],
          request: setRequestStateToFailure(),
        },
      ];
}

function handleAddProjectExternalLinkSucceeded(
  state: State,
  { project, link }: Boundaries["addProjectExternalLink"]["success"],
): State {
  const index = state.findIndex((res) => res.project.uuid === project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          links: [...state[index].links, link],
        },
        ...state.slice(index + 1),
      ]
    : state;
}

function handleRemoveProjectExternalLinkSucceeded(
  state: State,
  { project, link }: Boundaries["removeProjectExternalLink"]["success"],
): State {
  const index = state.findIndex((res) => res.project.uuid === project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          links: state[index].links.filter(({ name }) => name !== link.name),
        },
        ...state.slice(index + 1),
      ]
    : state;
}

function handleEditProjectExternalLinkSucceeded(
  state: State,
  { project, link, nextLink }: Boundaries["editProjectExternalLink"]["success"],
): State {
  const index = state.findIndex((res) => res.project.uuid === project.uuid);
  const linkIndex =
    index !== -1
      ? state[index].links.findIndex(({ index }) => index === link.index)
      : -1;

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          links:
            linkIndex !== -1
              ? [
                  ...state[index].links.slice(0, linkIndex),
                  nextLink,
                  ...state[index].links.slice(linkIndex + 1),
                ]
              : [...state[index].links, nextLink],
        },
        ...state.slice(index + 1),
      ]
    : state;
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveProjectExternalLinksRequested]: handleRequest,
    [events.retrieveProjectExternalLinksSucceeded]: handleSuccess,
    [events.retrieveProjectExternalLinksFailed]: handleFailure,
    [events.addProjectExternalLinkSucceeded]:
      handleAddProjectExternalLinkSucceeded,
    [events.removeProjectExternalLinkSucceeded]:
      handleRemoveProjectExternalLinkSucceeded,
    [events.editProjectExternalLinkSucceeded]:
      handleEditProjectExternalLinkSucceeded,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
